"use client";

import React, { useEffect, useState, useCallback } from "react";

import { Button, Form, Modal, Dropdown, Row, Col } from "react-bootstrap";
import { toast } from "react-toastify";

import {
  getAllSprints,
  postSprintTeam,
  getAllStory,
  movePendingStories,
} from "../../../src/pages/api/movependingstories.js";

const MovePendingStories = ({ show, onClose, sprint, onMoveSuccess }) => {
  const token = localStorage.getItem("token");
  const [sprints, setSprints] = useState([]);
  const [selectedSprint, setSelectedSprint] = useState(null);
  const [showCreateSprint, setShowCreateSprint] = useState(false);
  const [sprintName, setSprintName] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [pendingStories, setPendingStories] = useState([]);

  const fetchSprints = useCallback(async () => {
    try {
      const res = await getAllSprints(token);

      if (res.status === 200) {
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const futureSprints = res.data
          .filter((sp) => {
            const endDate = new Date(sp.endDate);
            endDate.setHours(0, 0, 0, 0);
            return endDate >= today;
          })
          .sort((a, b) => new Date(a.startDate) - new Date(b.startDate));

        setSprints(futureSprints);

        if (futureSprints.length > 0) {
          setSelectedSprint(futureSprints[0]);
        }
      }
    } catch (err) {
      toast.error("Failed to load sprints");
    }
  }, [token]);



  useEffect(() => {
    if (show) {
      fetchSprints();
    }
  }, [show, fetchSprints]);

  useEffect(() => {
    const fetchStories = async () => {
      try {
        const response = await getAllStory(token);
        const stories = response.data;

        const today = new Date().toISOString().split("T")[0];

        const expiredPending = stories.filter((story) => {
          const sprint = story.sprint;
          if (!sprint) return false;

          return sprint.endDate < today && story.rtsAccepted === false;
        });

        setPendingStories(expiredPending);
      } catch (error) {
        console.error("❌ Error fetching stories:", error);
        toast.error("Failed to load stories");
      }
    };

    if (show) fetchStories();
  }, [show, token]);

  const handleSaveSprint = async () => {
    setIsSaving(true);

    const payload = {
      sprintName,
      startDate,
      endDate,
    };

    try {
      const res = await postSprintTeam(payload, token);

      if (res.status === 200) {
        toast.success("Sprint created successfully");
        setShowCreateSprint(false);
        fetchSprints();
        window.dispatchEvent(new Event("sprintListUpdated"));
      } else {
        toast.error("Failed to create sprint");
      }
    } catch (err) {
      toast.error("Failed to create sprint");
    }

    setIsSaving(false);
  };

  const handleMove = async () => {
    if (!selectedSprint) {
      toast.error("Please select a sprint before moving!");
      return;
    }

    const currentSprintId = pendingStories[0]?.sprint?.id;
    if (!currentSprintId) {
        toast.error("Could not determine current sprint!");
        return;
    }

    const request = {
      storyIds: pendingStories.map((s) => s.id),
      currentSprintId: currentSprintId,
      nextSprintId: selectedSprint.id,
    };

    try {
      await movePendingStories(request, token);

      toast.success("Stories moved successfully!");
      localStorage.setItem("selectedSprint", JSON.stringify(selectedSprint));
      window.dispatchEvent(new Event("sprintChanged"));

      window.dispatchEvent(new Event("storyListUpdated"));
      window.dispatchEvent(new Event("sprintListUpdated"));

      onMoveSuccess();
      onClose();
    } catch (err) {
      toast.error("Failed to move stories");
      console.error(err);
    }
  };

  return (
    <>
      <Modal show={show} onHide={onClose} size="md" centered>
        <Modal.Header closeButton>
          <Modal.Title>Move Pending Stories</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Form.Group>
            <row className="d-flex justify-content-end">
              <Dropdown>
                <Dropdown.Toggle className="dropdown_button">
                  {selectedSprint ? selectedSprint.sprintName : "Select Sprint"}
                </Dropdown.Toggle>

                <Dropdown.Menu>
                  {sprints.length === 0 ? (
                    <Dropdown.Item disabled>No future sprints</Dropdown.Item>
                  ) : (
                    sprints.map((sp) => (
                      <Dropdown.Item
                        key={sp.id}
                        onClick={() => setSelectedSprint(sp)}
                      >
                        {sp.sprintName} (
                        {new Date(sp.startDate).toLocaleDateString()} –{" "}
                        {new Date(sp.endDate).toLocaleDateString()})
                      </Dropdown.Item>
                    ))
                  )}
                </Dropdown.Menu>
              </Dropdown>

              <Button
                variant="light"
                className="ms-2"
                onClick={() => setShowCreateSprint(true)}
              >
                <i className="bi bi-plus"></i>
              </Button>
            </row>

            <row className="table-responsive">
              <table className="table table-bordered text-center">
                <thead>
                  <tr>
                    <th style={{ background: "#6666FF", color: "#fff" }}>
                      Sr. No
                    </th>
                    <th style={{ background: "#6666FF", color: "#fff" }}>
                      Pending Story Title
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {pendingStories.length === 0 ? (
                    <tr>
                      <td colSpan="2">No pending stories</td>
                    </tr>
                  ) : (
                    pendingStories.map((story, index) => (
                      <tr key={story.id}>
                        <td>{index + 1}</td>
                        <td>{story.storyTitle}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </row>

            <row className="d-flex justify-content-end mt-3">
              <Button variant="primary" onClick={handleMove}>
                Move Stories
              </Button>
            </row>
          </Form.Group>
        </Modal.Body>
      </Modal>

      <Modal show={showCreateSprint} onHide={() => setShowCreateSprint(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Create Sprint</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Form>
            <Form.Label>Sprint Name</Form.Label>
            <Form.Control
              value={sprintName}
              onChange={(e) => setSprintName(e.target.value)}
              placeholder="Enter Sprint Name"
            />

            <Row className="mt-2">
              <Col>
                <Form.Label>Start Date</Form.Label>
                <Form.Control
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </Col>
            </Row>
            <Row className="mt-2">
              <Col>
                <Form.Label>End Date</Form.Label>
                <Form.Control
                  type="date"
                  min={startDate}
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </Col>
            </Row>
          </Form>
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowCreateSprint(false)}
          >
            Close
          </Button>

          <Button
            variant="primary"
            disabled={isSaving}
            onClick={handleSaveSprint}
          >
            {isSaving ? "Saving..." : "Save"}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default MovePendingStories;
